/**
 * API配置文件
 * 统一管理所有API端点和请求方法
 */

// API基础URL - 根据环境变量设置
const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8787" : "https://lunabrush.app")

// 开发模式标识 - 通过环境变量判断
const IS_DEV_MODE = process.env.NODE_ENV === "development" || process.env.NEXT_PUBLIC_DEV_MODE === "true"

// API版本
const API_VERSION = "v1"

/**
 * 统一的API请求方法
 * @param endpoint - API端点
 * @param options - 请求选项
 * @returns Promise<Response>
 */
async function apiRequest(endpoint: string, options: RequestInit = {}) {
  // 确保endpoint以/开头
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
  const url = `${API_BASE_URL}/api/v1${normalizedEndpoint}`

  // 设置默认请求头
  const defaultHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  }

  // 从localStorage获取认证token（如果存在）
  const token = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null
  if (token) {
    defaultHeaders["Authorization"] = `Bearer ${token}`
  }

  // 在开发模式下添加环境标识
  if (IS_DEV_MODE) {
    defaultHeaders["x-env"] = "development"
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  }

  try {
    console.log(`[API请求] ${config.method || "GET"} ${url}`)
    if (options.body) {
      try {
        console.log('[API请求] 请求体:', JSON.parse(options.body as string))
      } catch {
        console.log('[API请求] 请求体(非JSON):', options.body)
      }
    }
    const response = await fetch(url, config)
    const responseClone = response.clone()
    let responseData
    try {
      responseData = await responseClone.json()
      console.log('[API请求] 响应内容:', responseData)
    } catch {
      responseData = null
      console.log('[API请求] 响应内容(非JSON)')
    }

    // 如果响应不成功，抛出错误
    if (!response.ok) {
      throw new Error((responseData && responseData.message) || `HTTP error! status: ${response.status}`)
    }

    return response
  } catch (error) {
    console.error('[API请求] 发生错误:', error)
    throw error
  }
}

/**
 * 图像生成API接口
 */
export const imageAPI = {
  /**
   * 生成图像
   * @param prompt - 提示词
   * @param options - 生成选项
   * @returns Promise<生成结果>
   */
  async generate(
    prompt: string,
    options: {
      negativePrompt?: string
      style?: string
      colorTone?: string
      lighting?: string
      composition?: string
      aspectRatio?: string
      guidanceScale?: number
      highQuality?: boolean
      model?: string
    } = {},
  ) {
    const response = await apiRequest("generate", {
      method: "POST",
      body: JSON.stringify({
        prompt,
        negative_prompt: options.negativePrompt || "",
        style: options.style || "none",
        color_tone: options.colorTone || "none",
        lighting: options.lighting || "none",
        composition: options.composition || "none",
        aspect_ratio: options.aspectRatio || "1:1",
        guidance_scale: options.guidanceScale || 7.5,
        high_quality: options.highQuality || false,
        model: options.model || "lunabrush-v1",
      }),
    })
    return await response.json()
  },

  /**
   * 获取生成任务状态
   * @param taskId - 任务ID
   * @returns Promise<任务状态>
   */
  async getTaskStatus(taskId: string) {
    const response = await apiRequest(`/tasks/${taskId}`)
    return await response.json()
  },

  /**
   * 获取用户生成历史
   * @param page - 页码
   * @param limit - 每页数量
   * @returns Promise<生成历史>
   */
  async getHistory(page = 1, limit = 20) {
    const response = await apiRequest(`/history?page=${page}&limit=${limit}`)
    return await response.json()
  },
}

/**
 * 用户认证API接口
 */
export const authAPI = {
  /**
   * Google OAuth登录
   * @param code - Google授权码
   * @returns Promise<认证结果>
   */
  async googleLogin(code: string) {
    const response = await apiRequest("/auth/google", {
      method: "POST",
      body: JSON.stringify({ code }),
    })
    const data = await response.json()
    // 保存认证token到localStorage
    if (data.access_token) {
      localStorage.setItem("auth_token", data.access_token)
      localStorage.setItem("user_info", JSON.stringify(data.user))
    }
    return data
  },

  /**
   * GitHub OAuth登录
   * @param code - GitHub授权码
   * @returns Promise<认证结果>
   */
  async githubLogin(code: string) {
    const response = await apiRequest("/auth/github", {
      method: "POST",
      body: JSON.stringify({ code }),
    })
    const data = await response.json()
    // 保存认证token到localStorage
    if (data.access_token) {
      localStorage.setItem("auth_token", data.access_token)
      localStorage.setItem("user_info", JSON.stringify(data.user))
    }
    return data
  },

  /**
   * 用户登出
   * @returns Promise<void>
   */
  async logout() {
    await apiRequest("/auth/logout", {
      method: "POST",
    })
    // 清除本地存储的认证信息
    localStorage.removeItem("auth_token")
    localStorage.removeItem("user_info")
  },

  /**
   * 获取用户信息
   * @returns Promise<用户信息>
   */
  async getUserInfo() {
    const response = await apiRequest("/user/profile")
    return await response.json()
  },

  /**
   * 更新用户信息
   * @param userData - 用户数据
   * @returns Promise<更新结果>
   */
  async updateProfile(userData: { username?: string; email?: string }) {
    const response = await apiRequest("/user/profile", {
      method: "PUT",
      body: JSON.stringify(userData),
    })
    return await response.json()
  },
}

/**
 * 订阅API接口
 */
export const subscriptionAPI = {
  /**
   * 获取订阅计划
   * @returns Promise<订阅计划列表>
   */
  async getPlans() {
    const response = await apiRequest("/subscription/plans")
    return await response.json()
  },

  /**
   * 创建订阅
   * @param planId - 计划ID
   * @returns Promise<订阅结果>
   */
  async createSubscription(planId: string) {
    const response = await apiRequest("/subscription/create", {
      method: "POST",
      body: JSON.stringify({ plan_id: planId }),
    })
    return await response.json()
  },

  /**
   * 取消订阅
   * @returns Promise<取消结果>
   */
  async cancelSubscription() {
    const response = await apiRequest("/subscription/cancel", {
      method: "POST",
    })
    return await response.json()
  },

  /**
   * 获取用户订阅状态
   * @returns Promise<订阅状态>
   */
  async getSubscriptionStatus() {
    const response = await apiRequest("/subscription/status")
    return await response.json()
  },
}

/**
 * 系统API接口
 */
export const systemAPI = {
  /**
   * 获取系统状态
   * @returns Promise<系统状态>
   */
  async getStatus() {
    const response = await apiRequest("/system/status")
    return await response.json()
  },
}
